import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import type { TFunction } from "i18next";
import React, { useEffect } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Link,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useParams,
} from "react-router-dom";
import Explanation from "./components/Explanation";
import { Header } from "./components/Header";
import CalculatorSelector, {
  selectableCalculators,
} from "./components/laskurit/CalculatorSelector";
import ElakeArvioCalculator from "./components/laskurit/ElakeArvioCalculator";
import ElakePerhevapaanAjaltaCalculator from "./components/laskurit/ElakePerhevapaanAjaltaCalculator";
import OveCalculator from "./components/laskurit/OveCalculator";
import PerheElakeCalculator from "./components/laskurit/PerheElakeCalculator";
import YttariCalculator from "./components/yttari/YttariCalculator";
import { useDocumentMeta } from "./hooks/useDocumentMeta";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels,
  annotationPlugin,
);

const LocaleGuard = () => {
  const { i18n } = useTranslation();
  const { locale } = useParams();

  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  return <Outlet />;
};

const App: React.FC = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Body />}>
        <Route index element={<CalculatorSelector />} />
        <Route path=":locale" element={<LocaleGuard />}>
          <Route index element={<CalculatorSelector />} />
          <Route path="perhe-elakelaskuri" element={<PerheElakeCalculator />} />
          <Route
            path="elake-perhevapaan-ajalta-laskuri"
            element={<ElakePerhevapaanAjaltaCalculator />}
          />
          <Route
            path="yel-tyotulo-suositus-laskuri"
            element={<YttariCalculator />}
          />
          <Route path="elakelaskuri" element={<ElakeArvioCalculator />} />
          <Route
            path="osittaisen-vanhuuselakkeen-laskuri"
            element={<OveCalculator />}
          />
        </Route>
      </Route>,
    ),
    { basename: (globalThis as any).API_PREFIX || "/" },
  );
  return (
    <Container fluid className="main-container min-vh-100">
      <RouterProvider router={router} />
    </Container>
  );
};

const isDefined = (key: string, t: TFunction) => t(key) !== key;

const getExplanations = (i18nPrefix: string, t: TFunction) => {
  const explanations = [];
  for (let i = 1; ; i++) {
    const explanationKey = `${i18nPrefix}.aboutExplanation${i}`;
    if (!isDefined(explanationKey, t)) break; // stop when translation is missing
    explanations.push(explanationKey);
  }
  return explanations;
};
const Body: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const SUPPORTED_LOCALES = new Set(["fi", "sv", "en"]);

  const getLocaleFromPath = (path: string): string | null => {
    const parts = path.split("/");
    const locale = parts[1];
    return SUPPORTED_LOCALES.has(locale) ? locale : null;
  };

  const getCalculatorPathFromPath = (path: string): string => {
    const parts = path.split("/");
    if (SUPPORTED_LOCALES.has(parts[1])) {
      return parts.slice(2).join("/");
    }
    return ""; // fallback if no locale
  };

  const fullPath = location.pathname;

  const currentLocale = getLocaleFromPath(fullPath) || "fi"; // fallback to 'fi' if needed
  const calculatorPath = getCalculatorPathFromPath(fullPath); // may be empty string or '/calc1'
  const i18nPrefix =
    selectableCalculators.find((calc) => calc.pathName === calculatorPath)
      ?.i18nPrefix ?? "";
  const pageTitle = i18nPrefix
    ? `${t(`${i18nPrefix}.pageTitle`)} - ${t("pageTitle")}`
    : t("pageTitle");
  const description = i18nPrefix
    ? t(`${i18nPrefix}.metaDescription`)
    : t("metaDescription");

  return (
    <>
      <a
        className="skip-to-content sr-only sr-only-focusable"
        href="#main-content"
      >
        {t("skipToContent")}
      </a>
      {useDocumentMeta({
        title: pageTitle,
        description,
        lang: i18n.language,
      })}
      <Header pathPrefix={calculatorPath} />
      <main role="main" id="main-content" className="mt-4">
        {calculatorPath !== "" && (
          <div className="container content-container back-to-selector-link">
            <Link to={`/${currentLocale}`}>
              <span className="sr-only">{t("linkBackToSelector")}</span>
              <span aria-hidden="true">&lt; </span>
              {t("linkBackToSelector")}
            </Link>
          </div>
        )}
        <Outlet />
        {calculatorPath !== "" && (
          <Explanation explanationKeys={getExplanations(i18nPrefix, t)} />
        )}
      </main>
    </>
  );
};

export default App;
